import React, { useEffect, useState, useMemo } from 'react';
import {
  eachDayOfInterval,
  startOfMonth, endOfMonth,
  startOfWeek, endOfWeek,
  startOfYear, endOfYear,
  addMonths, subMonths,
  addWeeks, subWeeks,
  addYears, subYears,
  format,
  getWeek
} from 'date-fns';
import GradeCell from './GradeCell';
import api from '../services/api';

export default function DayCalendar({ userId, initialView = 'monthly', readOnly = false }) {
  const [grades, setGrades] = useState({});
  const [view, setView] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Calculate date range based on current view and selected date
  const { start, end, title } = useMemo(() => {
    switch (view) {
      case 'weekly':
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 0 }),
          end: endOfWeek(currentDate, { weekStartsOn: 0 }),
          title: `Week ${getWeek(currentDate)} - ${format(currentDate, 'yyyy')}`
        };
      case 'yearly':
        return {
          start: startOfYear(currentDate),
          end: endOfYear(currentDate),
          title: format(currentDate, 'yyyy')
        };
      case 'monthly':
      default:
        return {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
          title: format(currentDate, 'MMMM yyyy')
        };
    }
  }, [view, currentDate]);
  
  // Generate days array based on the date range
  const days = useMemo(() => 
    eachDayOfInterval({ start, end }), 
    [start, end]
  );

  // Navigation handlers
  const handlePrevious = () => {
    switch (view) {
      case 'weekly':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'yearly':
        setCurrentDate(subYears(currentDate, 1));
        break;
      case 'monthly':
      default:
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (view) {
      case 'weekly':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'yearly':
        setCurrentDate(addYears(currentDate, 1));
        break;
      case 'monthly':
      default:
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  // Fetch grades whenever the date range changes
  useEffect(() => {
    let isMounted = true;
    
    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');
    
    console.log(`Fetching grades: ${startStr} to ${endStr}`);
    
    api
      .get(`users/${userId}/grades/`, {
        params: {
          start: startStr,
          end: endStr
        },
      })
      .then(({ data }) => {
        if (!isMounted) return;
        
        console.log(`Received ${data.length} grades from API`);
        
        const map = {};
        data.forEach(({ year, month, day, grade }) => {
          const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          map[isoDate] = grade;
        });
        
        setGrades(map);
      })
      .catch((err) => {
        console.error('Failed to load grades:', err);
        setGrades({});
      });

    return () => {
      isMounted = false;
    };
  }, [userId, start, end]);

  // Calculate columns based on view
  const columns = view === 'yearly' ? '7fr '.repeat(7) : 'repeat(7, 1fr)';

  return (
    <div>
      {/* Navigation Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
        <button onClick={handlePrevious}>Previous</button>
        <h3>{title}</h3>
        <button onClick={handleNext}>Next</button>
      </div>
      
      {/* View Selector */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', border: '1px solid #ccc', borderRadius: '4px', padding: '0.5rem' }}>
          <button 
            onClick={() => setView('weekly')} 
            style={{ 
              background: view === 'weekly' ? '#007BFF' : '#f0f0f0', 
              color: view === 'weekly' ? 'white' : 'black',
              border: 'none', 
              padding: '0.5rem 1rem', 
              borderRadius: '4px',
              cursor: 'pointer' 
            }}
          >
            Week
          </button>
          <button 
            onClick={() => setView('monthly')} 
            style={{ 
              background: view === 'monthly' ? '#007BFF' : '#f0f0f0', 
              color: view === 'monthly' ? 'white' : 'black',
              border: 'none', 
              padding: '0.5rem 1rem', 
              borderRadius: '4px',
              cursor: 'pointer' 
            }}
          >
            Month
          </button>
          <button 
            onClick={() => setView('yearly')} 
            style={{ 
              background: view === 'yearly' ? '#007BFF' : '#f0f0f0', 
              color: view === 'yearly' ? 'white' : 'black',
              border: 'none', 
              padding: '0.5rem 1rem', 
              borderRadius: '4px',
              cursor: 'pointer' 
            }}
          >
            Year
          </button>
        </div>
      </div>
      
      {/* Calendar Header for Weekly/Monthly View */}
      {view !== 'yearly' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '0.5rem' }}>
          <div style={{ textAlign: 'center' }}>Sun</div>
          <div style={{ textAlign: 'center' }}>Mon</div>
          <div style={{ textAlign: 'center' }}>Tue</div>
          <div style={{ textAlign: 'center' }}>Wed</div>
          <div style={{ textAlign: 'center' }}>Thu</div>
          <div style={{ textAlign: 'center' }}>Fri</div>
          <div style={{ textAlign: 'center' }}>Sat</div>
        </div>
      )}
      
      {/* Calendar Grid */}
      <div 
        style={{ 
          display: 'grid', 
          gridTemplateColumns: columns,
          gap: '4px',
          gridAutoRows: view === 'yearly' ? '40px' : 'auto'
        }}
      >
        {Array.from({ length: start.getDay() }).map((_, i) => (
          <div key={`blank-${i}`} />
        ))}
        {days.map(day => {
          const iso = format(day, 'yyyy-MM-dd');
          return (
            <GradeCell
              key={iso}
              date={iso}
              currentGrade={grades[iso]}
              userId={userId}
              compact={view === 'yearly'}
              dayLabel={view === 'yearly' ? format(day, 'd') : null}
              monthLabel={view === 'yearly' && day.getDate() === 1 ? format(day, 'MMM') : null}
              readOnly={readOnly}
            />
          );
        })}
      </div>
    </div>
  );
}
